import { useState } from 'react';
import { Question, X } from '@phosphor-icons/react';
import { useResolve } from '@/infrastructure/useResolve';
import { type SourceKind } from '@/domain/beforered';
import { TOKEN_IColumnMapper } from '@/services/ColumnMapper';
import { useT } from '../useT';

const DESCS = (source: SourceKind, t: ReturnType<typeof useT>): string => {
  const map = {
    BOM: t.slotHelp.bom,
    INVENTORY: t.slotHelp.inventory,
    SALES: t.slotHelp.sales,
    MFG: t.slotHelp.mfg,
    PURCHASE: t.slotHelp.purchase,
    PARTS: t.slotHelp.parts,
  };
  return map[source];
};

interface Props {
  source: SourceKind;
}

export function SlotHelpButton({ source }: Props) {
  const [open, setOpen] = useState(false);
  const mapper = useResolve(TOKEN_IColumnMapper);
  const t = useT();
  const fields = mapper.spec(source);
  const required = fields.filter((f) => f.required);
  const optional = fields.filter((f) => !f.required);

  return (
    <div className="relative">
      <button
        type="button"
        aria-label={t.help.open}
        onClick={(e) => {
          e.stopPropagation();
          setOpen((v) => !v);
        }}
        className="flex h-5 w-5 items-center justify-center text-steel-400 hover:text-amber-signal"
      >
        <Question weight="bold" size={14} />
      </button>
      {open && (
        <div
          onClick={(e) => e.stopPropagation()}
          className="absolute right-0 top-7 z-30 w-72 border-2 border-amber-signal bg-white p-3 font-sans text-xs leading-relaxed text-steel-700 shadow-xl dark:bg-steel-900 dark:text-steel-200"
        >
          <header className="mb-2 flex items-start justify-between gap-2">
            <p>{DESCS(source, t)}</p>
            <button
              type="button"
              aria-label={t.help.close}
              onClick={() => setOpen(false)}
              className="shrink-0 text-steel-400 hover:text-steel-700 dark:hover:text-steel-200"
            >
              <X size={12} />
            </button>
          </header>
          {required.length > 0 && (
            <p className="mt-2 font-mono text-[11px]">
              <span className="font-bold text-light-green-deep dark:text-light-green">
                {t.slotHelp.requiredColumns}:
              </span>{' '}
              <span className="text-steel-600 dark:text-steel-300">
                {required.map((f) => f.name).join(', ')}
              </span>
            </p>
          )}
          {optional.length > 0 && (
            <p className="font-mono text-[11px]">
              <span className="font-bold text-steel-500">{t.slotHelp.optionalColumns}:</span>{' '}
              <span className="text-steel-600 dark:text-steel-300">
                {optional.map((f) => f.name).join(', ')}
              </span>
            </p>
          )}
        </div>
      )}
    </div>
  );
}
